import type { Device } from '../types/api';
import { calculateMacBasedSerial } from './deviceSerial';

/** Flat row shape used for CSV export. */
export interface DeviceCsvRow {
  name: string;
  status: string;
  registrationId: string;
  macAddress: string;
  secureSerial: string;
  secureCode: string;
  group: string;
  model: string;
  clientVersion: string;
  comnovoGatewayVersion: string;
  ipAddress: string;
  lastSeen?: string;
}

const FALLBACK_LAST_SEEN = '2020-01-01T07:00:00.00Z';
const GATEWAY_ENTITY_CLASS =
  'com.kiongroup.cport.safetyguarddos.infrastructure.persistence.models.GatewayEntity';

/** Build the flat rows consumed by {@link exportToCSV}. */
export const buildDeviceCsvRows = (devices: Device[]): DeviceCsvRow[] =>
  devices.map(device => ({
    name: device.name,
    status: device.status,
    registrationId: device.registrationId,
    macAddress: device.macAddress || '',
    secureSerial: device.secureSerial || '',
    secureCode: device.secureCode || '',
    group: device.groupName || '',
    model: device.hardware?.model || '',
    clientVersion: device.clientVersion || '',
    comnovoGatewayVersion: device.comnovoGatewayVersion || '',
    ipAddress: device.network?.ipAddress || '',
    lastSeen: device.lastSeen,
  }));

/** Build the MongoDB-collection-shaped documents consumed by {@link exportToJSON}. */
export const buildDeviceMongoDocs = (devices: Device[]) =>
  devices.map(device => ({
    macAddress: (device.macAddress || '').toUpperCase(),
    iotHubDeviceId: device.registrationId,
    fleetId: null,
    vendorSerial: calculateMacBasedSerial(device.macAddress),
    secureCode: device.secureCode || '',
    // The API sends a number despite the `string` type; the gateways collection stores text.
    secureSerial: device.secureSerial ? String(device.secureSerial) : '',
    lastSeen: {
      $date: device.lastSeen
        ? new Date(device.lastSeen).toISOString()
        : new Date(FALLBACK_LAST_SEEN).toISOString(),
    },
    positionX: 0,
    positionY: 0,
    positionZ: 0,
    _class: GATEWAY_ENTITY_CLASS,
  }));
